/**
 * NetlifyLogsService
 *
 * A service for connecting to Netlify logs via WebSocket.
 * Currently supports build logs only.
 */
export interface NetlifyLogsOptions {
  siteId: string;
  deployId: string;
  accessToken: string; // Add accessToken to options
  onMessage?: (message: any) => void;
  onError?: (error: Event) => void;
  onClose?: () => void;
  onOpen?: () => void;
}

export class NetlifyLogsService {
  private ws: WebSocket | null = null;
  private options: NetlifyLogsOptions;

  constructor(options: NetlifyLogsOptions) {
    this.options = options;
  }

  /**
   * Connect to Netlify logs WebSocket
   * @returns The WebSocket instance
   */
  connect(): WebSocket {
    // Close any existing connection
    this.disconnect();

    console.log(
      `[NetlifyLogsService] Connecting to logs - SiteID: ${this.options.siteId}, DeployID: ${this.options.deployId}`,
    );

    // Create a new WebSocket connection for build logs
    this.ws = new WebSocket('wss://socketeer.services.netlify.com/build/logs');

    // Set up event listeners
    this.ws.addEventListener('open', () => {
      console.log('[NetlifyLogsService] WebSocket connection opened, requesting access control token');
      this.getAccessControlToken()
        .then((accessToken) => {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            console.log('[NetlifyLogsService] Access token received, authenticating connection');

            // Send the payload with access token and required parameters
            this.ws.send(
              JSON.stringify({
                access_token: accessToken,
                deploy_id: this.options.deployId,
                site_id: this.options.siteId,
              }),
            );

            if (this.options.onOpen) {
              this.options.onOpen();
            } else {
              console.warn('[NetlifyLogsService] WebSocket not ready when token received. State:', this.ws?.readyState);
            }
          }
        })
        .catch((error) => {
          console.error('NetlifyLogsService failed to get access control token', error);

          if (this.options.onError) {
            this.options.onError(new ErrorEvent('error', { message: 'Failed to get access token' }));
          }
        });
    });

    this.ws.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data);

        if (this.options.onMessage) {
          this.options.onMessage(data);
        }
      } catch (error) {
        console.error('NetlifyLogsService failed to parse message', error);
      }
    });

    this.ws.addEventListener('close', () => {
      console.log('NetlifyLogsService: WebSocket connection closed');

      if (this.options.onClose) {
        this.options.onClose();
      }
    });

    this.ws.addEventListener('error', (error) => {
      console.error('NetlifyLogsService: WebSocket error', error);

      if (this.options.onError) {
        this.options.onError(error);
      }
    });

    return this.ws;
  }

  /**
   * Disconnect from the WebSocket
   */
  disconnect(): void {
    if (this.ws) {
      console.log('[NetlifyLogsService] Disconnecting WebSocket');
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Get the access control token from the API
   * @returns Promise that resolves to the access token
   */
  private async getAccessControlToken(): Promise<string> {
    const params = new URLSearchParams({
      siteId: this.options.siteId,
      deployId: this.options.deployId,
    });

    console.log('[NetlifyLogsService] Requesting access control token for', this.options.deployId);

    const response = await fetch(`/api/get-netlify-access-control-token?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${this.options.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get access token: ${response.status}`);
    }

    const data = (await response.json()) as { accessToken: string };
    console.log('[NetlifyLogsService] Successfully obtained access control token');

    return data.accessToken;
  }

  /**
   * Check if the WebSocket is connected
   * @returns True if connected, false otherwise
   */
  isConnected(deployId?: string): boolean {
    return (
      this.ws !== null && this.ws.readyState === WebSocket.OPEN && (!deployId || this.options.deployId === deployId)
    );
  }
}
